/**
 * App shell.
 *
 * Layout: collapsible left nav (220px expanded / 60px collapsed) + 56px top bar
 * + content area filling the rest. Stays mounted across route changes via
 * TanStack Router's outlet.
 *
 * E19: sidebar collapse state persists to localStorage so it survives reload.
 */
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  ClipboardList,
  Clock,
  Coins,
  Columns3,
  Factory,
  FileText,
  Home,
  Inbox,
  LayoutGrid,
  LogOut,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  ScanSearch,
  Search,
  Settings,
  Users,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useCurrentUser, useLogout } from '@/lib/auth';
import { GlobalSearchModal } from '@/components/global-search-modal';
import { KeyboardShortcutsHelp } from '@/components/keyboard-shortcuts-help';
import { NotificationBell } from '@/components/notification-bell';
import { RefreshBanner } from '@/components/refresh-banner';
import { StorageBanner } from '@/components/storage-banner';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    // Storage unavailable (Safari private, etc) — fall through to default
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage unavailable — silently no-op
  }
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  /** When true, this item only renders for ADMIN users. */
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Home },
  { to: '/awaiting-action', label: 'Awaiting action', icon: Clock },
  { to: '/kanban', label: 'Project board', icon: LayoutGrid },
  { to: '/entity-kanban', label: 'Entity board', icon: Columns3 },
  { to: '/alerts', label: 'Alerts', icon: AlertTriangle },
  { to: '/forecast', label: 'Forecast', icon: CalendarClock },
  { to: '/receivables', label: 'Receivables', icon: Coins },
  { to: '/communications', label: 'Communications', icon: MessageSquare },
  { to: '/suppliers', label: 'Suppliers', icon: Factory },
  { to: '/clients', label: 'Clients', icon: Building2 },
  { to: '/projects', label: 'Projects', icon: ClipboardList },
  { to: '/inbox', label: 'Inbox', icon: Inbox, adminOnly: true },
  { to: '/audit', label: 'Audit Log', icon: FileText, adminOnly: true },
  { to: '/users', label: 'Users', icon: Users, adminOnly: true },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function AppShell() {
  const userQuery = useCurrentUser();
  const user = userQuery.data?.user ?? null;
  const isAdmin = user?.role === 'ADMIN';
  const logout = useLogout();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsedPreference());

  useEffect(() => {
    writeCollapsedPreference(collapsed);
  }, [collapsed]);

  return (
    <div className="flex h-full">
      {/* Left nav — width animates between collapsed (60px) and expanded (220px) */}
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-border-default bg-sidebar transition-[width] duration-150 ease-out',
          collapsed ? 'w-[60px]' : 'w-[220px]',
        )}
      >
        <div
          className={cn(
            'flex h-14 items-center gap-2.5 border-b border-border-default',
            collapsed ? 'justify-center px-0' : 'px-4',
          )}
        >
          {/* Patch P22 — rebrand to TenderLens. `ScanSearch` is a lucide
           * icon that reads as "a lens scanning content" — a literal
           * (but tasteful) visual reference to the product name. Kept
           * inside the magenta chip so the signature mark stays
           * recognisable across the chrome. */}
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent text-accent-fg shadow-sm">
            <ScanSearch className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </div>
          {!collapsed ? (
            <span className="truncate text-[15px] font-semibold tracking-tight text-primary">
              TenderLens
            </span>
          ) : null}
        </div>
        <nav className={cn('flex-1 py-3', collapsed ? 'px-1.5' : 'px-2')}>
          {navItems
            .filter((item) => !item.adminOnly || isAdmin)
            .map((item) => (
              <NavLink key={item.to} item={item} collapsed={collapsed} />
            ))}
        </nav>
        <div className={cn('border-t border-border-default', collapsed ? 'p-2' : 'p-3')}>
          {user ? (
            collapsed ? (
              <button
                type="button"
                onClick={() =>
                  logout.mutate(undefined, {
                    onSuccess: () => void navigate({ to: '/login' }),
                  })
                }
                title={`${user.displayName} — sign out`}
                aria-label={`${user.displayName} — sign out`}
                className="flex h-8 w-full items-center justify-center rounded-full bg-subtle text-xs font-semibold text-secondary hover:bg-border-subtle"
              >
                {user.displayName.slice(0, 2).toUpperCase()}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-subtle text-xs font-semibold text-secondary">
                  {user.displayName.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-primary">
                    {user.displayName}
                  </div>
                  <div className="truncate text-xs text-secondary">
                    {user.role === 'ADMIN' ? 'Administrator' : 'Assistant'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    logout.mutate(undefined, {
                      onSuccess: () => void navigate({ to: '/login' }),
                    })
                  }
                  aria-label="Sign out"
                  className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-secondary"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            )
          ) : null}
        </div>
        {/* Patch P22 — vendor attribution. Kept very small and tertiary so
         * it's never competing with content, but always present on every
         * page of the app. Hidden in collapsed sidebar (no room) — login
         * page footer carries the canonical attribution. */}
        {!collapsed ? (
          <div className="border-t border-border-default px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
            Powered by Solutyics
          </div>
        ) : null}
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <StorageBanner />
        <RefreshBanner />
        <TopBar collapsed={collapsed} onToggleSidebar={() => setCollapsed((v) => !v)} />
        <main className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Patch P17 — sidebar toggle moved into the TopBar, at its left edge.
 * Sits in a single, fixed location regardless of sidebar state — the
 * standard pattern users expect (Notion, Linear, GitHub, etc.). The
 * icon swaps between `PanelLeftOpen` (when collapsed → click to open)
 * and `PanelLeftClose` (when expanded → click to close), so the icon
 * itself tells the user what the next state will be.
 */
function SidebarToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <button
      type="button"
      onClick={onToggle}
      title={collapsed ? 'Open sidebar' : 'Close sidebar'}
      aria-label={collapsed ? 'Open sidebar' : 'Close sidebar'}
      aria-expanded={!collapsed}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-secondary hover:bg-subtle hover:text-primary"
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
    </button>
  );
}

function NavLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const routerState = useRouterState({ select: (s) => s.location.pathname });
  const isActive = item.to === '/' ? routerState === '/' : routerState.startsWith(item.to);
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      // E19: in collapsed mode, label hidden but exposed via the native title
      // attribute (hover tooltip) and aria-label (screen readers).
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      // Patch P21 — active state is now a dark slate "selected tab"
      // (universal pattern across Linear, Notion, GitHub). The prior
      // magenta-tinted active state spread the accent colour too widely;
      // when brand colour appears everywhere it stops feeling distinctive
      // and starts feeling pastel. Now the accent (magenta) is reserved
      // for commands (buttons, brand icon, status badges) and navigation
      // uses contrast (dark on light) as the "you are here" cue instead.
      className={cn(
        'flex items-center rounded-md text-sm transition-colors',
        collapsed ? 'h-8 justify-center px-0' : 'gap-2 px-2 py-1.5',
        isActive
          ? 'bg-primary font-medium text-white'
          : 'text-secondary hover:bg-subtle hover:text-primary',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
    </Link>
  );
}

function TopBar({
  collapsed,
  onToggleSidebar,
}: {
  collapsed: boolean;
  onToggleSidebar: () => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  // Patch 5 — `?` shortcut opens the keyboard-help dialog.
  const [helpOpen, setHelpOpen] = useState(false);

  // ⌘K / Ctrl-K — global shortcut to open the search modal.
  // `?` — open keyboard help.
  // Both guard against firing while the user is typing into a field; the
  // search modal manages its own key handling once open, so we only need
  // to gate by the focused element being editable.
  useEffect(() => {
    function isEditableTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.isContentEditable) return true;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    }
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      // `?` is Shift + / on most keyboards; the resulting `e.key` is '?'
      // directly. Skip if any modifier is held (so ⌘? doesn't fire) and
      // if the user is editing text.
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        setHelpOpen(true);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border-default bg-surface px-4">
      <SidebarToggle collapsed={collapsed} onToggle={onToggleSidebar} />
      <SearchAffordance onClick={() => setSearchOpen(true)} />
      <div className="flex-1" />
      <NotificationBell />
      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <KeyboardShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

function SearchAffordance({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 w-[420px] max-w-full items-center gap-2 rounded-md border border-border-default bg-base px-3 text-sm text-tertiary transition-colors hover:bg-subtle"
    >
      <Search className="h-4 w-4" strokeWidth={1.5} />
      <span className="flex-1 text-left">Search projects, parts, suppliers…</span>
      <kbd className="rounded-sm border border-border-default bg-surface px-1.5 py-0.5 text-[10px] font-medium text-secondary">
        ⌘K
      </kbd>
    </button>
  );
}

/** Renders just the outlet, used inside the auth boundary. */
export function ShellContent({ children }: { children: ReactNode }) {
  return <div className="px-8 py-6">{children}</div>;
}
